import { DAYS_YEAR } from "@/constants/general"
import dayjs, { Dayjs } from "dayjs"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"

dayjs.extend(isSameOrBefore)
import {
  IProductDetails,
  ITariff,
  IUnitRates,
} from "@/models/tariff/tariff.types"
import { IAgreement } from "@/models/agreement/agreement.types"
import { MeterpointTariffs } from "@/types/MeterpointTariffs"
import {
  COOLING_OFF_PERIOD,
  DEFAULT_RENEWAL_WINDOW_PERIOD,
} from "@/constants/finance"
import { IUsageEstimation } from "@/services/UsageEstimationService/types"
import { ITariffCompared } from "@/models/tariffCompared/tariffCompared.types"

export const RenewalStartAlignment = Object.freeze({
  FutureStartDate: "FutureStartDate",
  Today: "Today",
  EndDate: "EndDate",
} as const)

export type RenewalStartAlignmentType =
  (typeof RenewalStartAlignment)[keyof typeof RenewalStartAlignment]

type IRegisterUsage = {
  [meterpointId: string]: {
    [registerId: string]: {
      rateName: string | null
      usage: number
    }
  }
}

type IAnnualCosts = {
  currentAgreementCost: number
  selectedAgreementCost: number
  importTariffCosts: Record<string, number>
}

export type RenewalStartDate = {
  date: Dayjs
  alignedTo: RenewalStartAlignmentType
}

interface ILoadFactor {
  Standard: number
  Day?: number
  Night?: number

  [key: string]: number | undefined
}

const tariffHelper = {
  annualCost(
    agreement: ITariff,
    registers: { usage: number; rateName: string | null }[],
    monthlyRateComparison?: ITariffCompared,
  ) {
    const differentNextMonthRates =
      monthlyRateComparison?.hasDifferentUnitRates && agreement.isVariable

    const unitRates = differentNextMonthRates
      ? monthlyRateComparison.nextMonth?.unitRates
      : agreement.productDetails.unitRates

    const unitRate = differentNextMonthRates
      ? monthlyRateComparison.nextMonth?.unitRate
      : agreement.productDetails.unitRate

    return tariffHelper.timePeriodCost({
      isGas: agreement.isGas,
      standingCharge: agreement.productDetails.standingCharge,
      days: DAYS_YEAR,
      unitRates,
      unitRate,
      registers,
    })
  },
  getAnnualCosts(
    tariffAgreements: MeterpointTariffs,
    registerUsage: IRegisterUsage,
    selectedTariffs: Record<string, ITariff> = {},
  ) {
    const annualCosts: Record<string, IAnnualCosts> = {}

    for (const [meterpointId, meterpointTariffs] of Object.entries(
      tariffAgreements,
    )) {
      if (!meterpointTariffs.publiclyAvailableTariffList) continue

      const registers = Object.values(registerUsage[meterpointId])
      const currentAgreementCost = meterpointTariffs.currentTariff
        ? tariffHelper.annualCost(
            meterpointTariffs.currentTariff,
            registers,
            meterpointTariffs.monthlyRateComparison,
          )
        : 0

      const monthlyRateComparison =
        selectedTariffs[meterpointId]?.id ===
        meterpointTariffs.currentTariff?.id
          ? meterpointTariffs.monthlyRateComparison
          : undefined

      const selectedAgreementCost = selectedTariffs[meterpointId]
        ? tariffHelper.annualCost(
            selectedTariffs[meterpointId],
            registers,
            monthlyRateComparison,
          )
        : 0

      const publiclyAvailableTariffList = [
        ...meterpointTariffs.publiclyAvailableTariffList,
      ]
      const currentTariffExists = publiclyAvailableTariffList.find(
        (tariff) => tariff.id === meterpointTariffs.currentTariff?.id,
      )
      if (!currentTariffExists) {
        if (meterpointTariffs.currentTariff) {
          publiclyAvailableTariffList.push(meterpointTariffs.currentTariff)
        }
      }

      const importTariffCosts = publiclyAvailableTariffList
        .filter((tariff) => !tariff.isExport)
        .reduce<Record<number, number>>((acc, tariff) => {
          const tariffId = Number(tariff.id)
          acc[tariffId] = tariffHelper.annualCost(tariff, registers)
          return acc
        }, {})

      annualCosts[meterpointId] = {
        currentAgreementCost,
        selectedAgreementCost,
        importTariffCosts,
      }
    }

    return annualCosts
  },
  isFutureStartDateTariff(productDetails: IProductDetails) {
    const now = dayjs()
    return productDetails.fromDttm.isAfter(now, "day")
  },
  timePeriodCost(args: {
    isGas: boolean
    standingCharge: number
    unitRates: IUnitRates | undefined
    unitRate: number | undefined
    registers: { usage: number; rateName: string | null }[]
    days: number
    loadFactor?: ILoadFactor
  }) {
    if (!args.registers.length) return 0
    const loadFactor = args.loadFactor ?? { Standard: 1, Day: 1, Night: 1 }
    const standingChargeCost = (args.standingCharge * args.days) / 100
    let consumptionCost = 0

    if (args.isGas) {
      const yearlyUsage = Number(args.registers[0].usage)
      const weightedGasUsage = yearlyUsage * loadFactor.Standard
      consumptionCost = (args.unitRate ?? 0) * weightedGasUsage
    } else {
      args.registers.forEach((register) => {
        const unitRate: number =
          args.unitRates?.[register.rateName ?? "Standard"] ??
          args.unitRates?.Standard

        const loadFactorElec = loadFactor[register.rateName ?? "Standard"] ?? 1
        const weightedElecUsage = Number(register.usage) * loadFactorElec
        consumptionCost += unitRate * weightedElecUsage
      })
    }
    return standingChargeCost + consumptionCost / 100
  },
  isGas(code: string) {
    const splitCode = code.split("-")
    return splitCode[0].charAt(0) === "G" || splitCode[1].charAt(0) === "G"
  },
  getBundleCode(code: string) {
    return code.split("-").slice(1).join("-")
  },
  getRenewalStartDateForAgreement(
    newAgreement: ITariff,
    activeAgreement: IAgreement,
  ): RenewalStartDate {
    const currentAgreementEndDate = activeAgreement.endDate
    const newTariffStartDate = newAgreement.productDetails.fromDttm

    const hasVariableOrEndedAgreement =
      !currentAgreementEndDate ||
      dayjs(currentAgreementEndDate).isBefore(dayjs(), "day")

    let date = null as dayjs.Dayjs | null
    let alignedTo = null as RenewalStartAlignmentType | null
    if (
      !this.isFutureStartDateTariff(newAgreement.productDetails) &&
      hasVariableOrEndedAgreement
    ) {
      date = dayjs()
      alignedTo = RenewalStartAlignment.Today
    } else if (
      !currentAgreementEndDate ||
      dayjs(currentAgreementEndDate).isBefore(dayjs(newTariffStartDate))
    ) {
      date = dayjs(newTariffStartDate)
      alignedTo = RenewalStartAlignment.FutureStartDate
    } else {
      date = dayjs(currentAgreementEndDate)
      alignedTo = RenewalStartAlignment.EndDate
    }
    return { date, alignedTo }
  },

  getDefaultRenewalStart(
    tariffAgreements: MeterpointTariffs,
    selectedTariffs: Record<string, ITariff>,
  ) {
    const renewalStartDates = Object.entries(tariffAgreements)
      .reduce((dates, [meterpointId, meterpoint]) => {
        const newAgreement = selectedTariffs[meterpointId]
        if (meterpoint.activeAgreement) {
          dates.push(
            tariffHelper.getRenewalStartDateForAgreement(
              newAgreement,
              meterpoint.activeAgreement,
            ),
          )
        }

        return dates
      }, [] as RenewalStartDate[])
      .sort(({ date: date1 }, { date: date2 }) =>
        dayjs(date1).isAfter(date2) ? -1 : 1,
      )
    return renewalStartDates[0]
  },

  getAgreementEndDates(tariffAgreements: MeterpointTariffs) {
    const endDates = Object.values(tariffAgreements).reduce(
      (meterpoints, meterpoint) => {
        if (meterpoint.activeAgreement) {
          meterpoints.push(
            meterpoint.activeAgreement.endDate === null
              ? null
              : meterpoint.activeAgreement.endDate,
          )
        }
        return meterpoints
      },
      [] as (Dayjs | null)[],
    )

    // Transform to string format to use Set and get an array of unique dates
    const endDatesStringFormat = endDates
      .filter((date) => date !== null)
      .map((date) => date?.toString())
    // transform unique dates from string to dayjs format
    return [...new Set(endDatesStringFormat)].map((date) => dayjs(date))
  },

  getExitFeeTotal(
    tariffAgreements: MeterpointTariffs,
    changeDate: Date | null,
    renewalPeriod: number,
  ) {
    const tariffAgreementsList = Object.values(tariffAgreements)
    const agreementsForExitFees = tariffAgreementsList
      .filter(
        (meterpoint) =>
          //check if current date is past cooling-off period
          dayjs()
            .startOf("day")
            .diff(meterpoint.activeAgreement?.orderedDate, "days") >
            COOLING_OFF_PERIOD ||
          // check if start date is past cooling-off period
          dayjs(changeDate).diff(
            meterpoint.activeAgreement?.orderedDate,
            "days",
          ) > COOLING_OFF_PERIOD,
      )
      // check if is in renewal period
      .filter((meterpoint) => {
        return (
          dayjs().diff(meterpoint.activeAgreement?.endDate, "days") <
            -renewalPeriod ||
          dayjs(changeDate).diff(meterpoint.activeAgreement?.endDate, "days") <
            -DEFAULT_RENEWAL_WINDOW_PERIOD
        )
      })

    // if one of the agreements is in cooling-off period or in renewal period we don't take any exit fee
    if (
      agreementsForExitFees.length === 0 ||
      agreementsForExitFees.length < tariffAgreementsList.length
    ) {
      return 0
    }

    return agreementsForExitFees.reduce((fees, meterpoint) => {
      if (meterpoint.currentTariff) {
        fees += meterpoint.currentTariff.productDetails.earlyTerminationCharge
      }
      return fees
    }, 0)
  },
  calculateBasicAnnualCost(tariff: ITariff, usage: Partial<IUsageEstimation>) {
    const standingChargeCost =
      ((tariff.productDetails.standingCharge ?? 0) * DAYS_YEAR) / 100
    const consumptionCost = tariffHelper.calculateBasicConsumptionCost(
      tariff,
      usage,
    )

    return standingChargeCost + consumptionCost / 100
  },
  calculateBasicConsumptionCost(
    tariff: ITariff,
    usage: Partial<IUsageEstimation>,
  ) {
    if (tariff.isGas && typeof usage.gas === "number") {
      return (tariff.productDetails.unitRate ?? 0) * usage.gas
    } else if (tariff.isOneRate && typeof usage.electricity === "number") {
      return (
        (tariff.productDetails.unitRates?.Standard ?? 0) * usage.electricity
      )
    } else if (
      tariff.isOneRate &&
      (typeof usage.electricityDay === "number" ||
        typeof usage.electricityNight === "number")
    ) {
      return (
        (tariff.productDetails.unitRates?.Standard ?? 0) *
        ((usage.electricityDay ?? 0) + (usage.electricityNight ?? 0))
      )
    } else if (
      typeof usage.electricityDay === "number" ||
      typeof usage.electricityNight === "number"
    ) {
      const dayCost =
        (tariff.productDetails.unitRates?.Day ?? 0) *
        (usage.electricityDay ?? 0)
      const nightCost =
        (tariff.productDetails.unitRates?.Night ?? 0) *
        (usage.electricityNight ?? 0)
      return dayCost + nightCost
    }

    return 0
  },
}

export default tariffHelper
